import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/apiEndpoints';
import { AUTH_TYPE } from './Types';
import {
  AUTH_TOKEN,
  USER_GITHUB_USERNAME,
  USER_ID,
  USER_NAME,
  USER_PHONE,
  USER_ROLE,
  USER_STATUS,
  USER_GITHUB_PROFILE,
} from '../../../configs/configs';
import { fetchGithubUserProfile } from '../../../services/githubService';
import { clearSession, notifySessionChanged } from '../../../utils/session';
import { getErrorMessage } from '../../../utils/apiError';

const store = (key, value) => {
  try {
    localStorage.setItem(key, value ?? '');
  } catch {
    // Storage unavailable (private mode); the session still works for this page.
  }
};

// The GitHub avatar shown on the profile page. Not needed to sign in, so it's never awaited.
const rememberGithubProfile = username => {
  if (!username) return;
  fetchGithubUserProfile(username)
    .then(result => {
      if (result.success && result.data) store(USER_GITHUB_PROFILE, JSON.stringify(result.data));
    })
    .catch(() => {});
};

export const loginAsync = async ({ dispatch, phoneNumber, password }) => {
  dispatch?.({ type: AUTH_TYPE.IS_LOADING, payload: true });
  try {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, { phoneNumber, password });
    const { token, user } = response?.data?.data || {};

    if (!response?.data?.success || !token) {
      const message = response?.data?.message || 'Could not sign you in. Please try again.';
      dispatch?.({ type: AUTH_TYPE.LOGIN_FAILED, payload: message });
      return { success: false, message };
    }

    const name = user?.fullName || user?.name || '';
    store(AUTH_TOKEN, token);
    store(USER_NAME, name);
    store(USER_GITHUB_USERNAME, user?.gitHubUsername);
    store(USER_ID, user?.id);
    store(USER_PHONE, user?.phone || user?.phoneNumber);
    store(USER_ROLE, user?.role);
    store(USER_STATUS, user?.status);
    notifySessionChanged();
    rememberGithubProfile(user?.gitHubUsername);

    dispatch?.({ type: AUTH_TYPE.LOGIN_SUCCESS, payload: user });
    dispatch?.({ type: AUTH_TYPE.NAME, payload: name });
    dispatch?.({ type: AUTH_TYPE.ID, payload: user?.id ?? '' });
    dispatch?.({ type: AUTH_TYPE.ROLE, payload: user?.role || '' });
    return { success: true, data: user };
  } catch (error) {
    const message = getErrorMessage(error, 'Could not sign you in. Please try again.');
    dispatch?.({ type: AUTH_TYPE.LOGIN_FAILED, payload: message });
    return { success: false, message };
  } finally {
    dispatch?.({ type: AUTH_TYPE.IS_LOADING, payload: false });
  }
};

export const registerAsync = async ({ dispatch, name, phoneNumber, email, githubUsername, password }) => {
  dispatch?.({ type: AUTH_TYPE.IS_LOADING, payload: true });
  try {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, {
      fullName: name,
      githubUsername,
      phoneNumber,
      email: email || null, // receipts, delivery emails and password reset
      password,
    });

    if (response?.data?.success === false) {
      return { success: false, message: response.data.message || 'Could not create your account.' };
    }
    return {
      success: true,
      data: response?.data?.data,
      message: response?.data?.message,
    };
  } catch (error) {
    return { success: false, message: getErrorMessage(error, 'Could not create your account. Please try again.') };
  } finally {
    dispatch?.({ type: AUTH_TYPE.IS_LOADING, payload: false });
  }
};

/** Ends the session everywhere (storage, navbar, app state). */
export const logoutAsync = dispatch => {
  clearSession();
  dispatch?.({ type: AUTH_TYPE.LOGOUT });
  return { success: true };
};

/** After the profile changes: keep the stored name/username, app state and navbar in step. */
export const applyProfileUpdate = (dispatch, user) => {
  if (!user) return;
  if (user.fullName) {
    store(USER_NAME, user.fullName);
    dispatch?.({ type: AUTH_TYPE.NAME, payload: user.fullName });
  }
  if (user.gitHubUsername) {
    let previous = null;
    try {
      previous = localStorage.getItem(USER_GITHUB_USERNAME);
    } catch {
      // Storage unavailable.
    }
    store(USER_GITHUB_USERNAME, user.gitHubUsername);
    if (previous !== user.gitHubUsername) rememberGithubProfile(user.gitHubUsername);
  }
  notifySessionChanged();
};
